
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Video, 
  Mic, 
  Clock, 
  User, 
  MessageSquare, 
  FileText, 
  X,
  Play,
  Pause,
  Save,
  ArrowLeft
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { UnifiedSessionRecording } from '@/components/recording/UnifiedSessionRecording';
import { ZoomMeetingInfo } from '@/components/dashboard/shared/ZoomMeetingInfo';
import { ChatButton } from '@/components/dashboard/shared/ChatButton';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { UnifiedError } from '@/components/dashboard/shared/UnifiedError';

interface SessionFunctionPageProps {}

export const SessionFunctionPage: React.FC<SessionFunctionPageProps> = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { markMessagesAsRead, unreadCounts } = useUnreadMessages({ 
    userId: undefined, // Will be set by the hook from auth context
    userType: 'practitioner'
  });
  
  const [booking, setBooking] = useState<any>(null);
  const [client, setClient] = useState<any>(null);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [sessionNotes, setSessionNotes] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  // 获取预约信息
  useEffect(() => {
    const fetchBookingData = async () => {
      if (!bookingId || !user) return;

      try {
        console.log('🔍 Fetching booking data for:', bookingId);
        
        const { data: bookingData, error: bookingError } = await supabase
          .from('bookings')
          .select(`
            *,
            clients(*)
          `)
          .eq('id', bookingId)
          .eq('practitioner_id', user.id)
          .single();

        if (bookingError) {
          console.error('获取预约信息错误:', bookingError);
          toast({
            title: "错误",
            description: "无法获取预约信息，请检查权限",
            variant: "destructive"
          });
          return;
        }

        console.log('✅ Booking data fetched:', bookingData);
        setBooking(bookingData);
        setClient(bookingData.clients);
      } catch (error) {
        console.error('获取数据错误:', error);
        toast({
          title: "错误",
          description: "获取预约数据时发生错误",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookingData();
  }, [bookingId, user, toast]);

  // 计时器
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (isSessionActive && sessionStartTime) {
      interval = setInterval(() => {
        const now = new Date();
        const diff = now.getTime() - sessionStartTime.getTime();
        setSessionDuration(Math.floor(diff / 1000));
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isSessionActive, sessionStartTime]);

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartSession = () => {
    setIsSessionActive(true);
    setSessionStartTime(new Date());
    toast({
      title: "疗程开始",
      description: "疗程计时已开始",
    });
  };

  const handleEndSession = () => {
    setIsSessionActive(false);
    toast({
      title: "疗程结束",
      description: `疗程时长: ${formatDuration(sessionDuration)}`,
    });
  };

  const handleSaveNotes = async () => {
    if (!sessionNotes.trim()) {
      toast({
        title: "提示",
        description: "请输入笔记内容",
        variant: "destructive"
      });
      return;
    }

    setIsSavingNotes(true);
    try {
      // 保存疗程笔记到数据库 - 这里可以扩展保存到session_transcripts或新的notes表
      console.log('💾 Saving session notes:', sessionNotes);
      
      toast({
        title: "笔记已保存",
        description: "疗程笔记已成功保存",
      });
    } catch (error) {
      console.error('保存笔记错误:', error);
      toast({
        title: "保存失败",
        description: "无法保存笔记，请重试",
        variant: "destructive"
      });
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handleClose = () => {
    navigate('/practitioner-dashboard');
  };

  const handleChatOpened = () => {
    if (bookingId) {
      markMessagesAsRead(bookingId);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">加载中...</div>
      </div>
    );
  }

  if (!booking || !client) {
    return (
      <UnifiedError
        title="未找到预约信息"
        message="无法获取预约或客户信息，请确保您有权限访问此预约。"
        retryText="返回控制面板"
        onRetry={handleClose}
        fullScreen={true}
      />
    );
  }

  const unreadCount = unreadCounts.unreadByBooking[bookingId!] || 0;

  return (
    <div className="min-h-screen bg-background p-4">
      {/* 顶部栏 */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold">疗程管理</h1>
          <Badge variant={isSessionActive ? "destructive" : "secondary"}>
            {isSessionActive ? "进行中" : "待开始"}
          </Badge>
          {isSessionActive && (
            <div className="flex items-center gap-2 text-lg font-mono">
              <Clock className="h-5 w-5" />
              {formatDuration(sessionDuration)}
            </div>
          )}
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleClose}
        >
          <X className="h-4 w-4 mr-2" />
          关闭
        </Button>
      </div>

      {/* 主要内容区 */}
      <div className="grid grid-cols-12 gap-6 h-[calc(100vh-120px)]">
        {/* 左侧：客户信息 */}
        <div className="col-span-3">
          <Card className="h-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                客户信息
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-medium">{client.first_name} {client.last_name}</h3>
                <p className="text-sm text-muted-foreground">Client Profile</p>
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <div>
                  <span className="text-sm font-medium">预约时间:</span>
                  <p className="text-sm">{new Date(booking.start_time).toLocaleString('zh-CN')}</p>
                </div>
                <div>
                  <span className="text-sm font-medium">服务类型:</span>
                  <p className="text-sm">{booking.format}</p>
                </div>
                <div>
                  <span className="text-sm font-medium">状态:</span>
                  <Badge variant="outline">{booking.status}</Badge>
                </div>
              </div>

              <Separator />

              {/* 疗程控制 */}
              <div className="space-y-2">
                {!isSessionActive ? (
                  <Button 
                    onClick={handleStartSession}
                    className="w-full"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    开始疗程
                  </Button>
                ) : (
                  <Button 
                    onClick={handleEndSession}
                    variant="destructive"
                    className="w-full"
                  >
                    <Pause className="h-4 w-4 mr-2" />
                    结束疗程
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* 中央：主要功能区 */}
        <div className="col-span-6 space-y-4">
          {/* Zoom视频区域 */}
          <Card className="h-1/3">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <Video className="h-5 w-5" />
                视频通话
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ZoomMeetingInfo 
                booking={booking}
                userRole="practitioner"
              />
            </CardContent>
          </Card>

          {/* 录音区域 */}
          <Card className="h-2/3">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <Mic className="h-5 w-5" />
                疗程录音
              </CardTitle>
            </CardHeader>
            <CardContent className="h-[calc(100%-80px)]">
              {user && client && (
                <UnifiedSessionRecording
                  bookingId={bookingId!}
                  practitionerId={user.id}
                  clientId={client.id}
                  onTranscriptSaved={(transcriptId) => {
                    console.log('转录保存成功:', transcriptId);
                    toast({
                      title: "录音保存成功",
                      description: `转录ID: ${transcriptId}`,
                    });
                  }}
                />
              )}
            </CardContent>
          </Card>
        </div>

        {/* 右侧：聊天和笔记 */}
        <div className="col-span-3 space-y-4">
          {/* 疗程笔记 */}
          <Card className="h-1/2">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                疗程笔记
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <textarea
                value={sessionNotes}
                onChange={(e) => setSessionNotes(e.target.value)}
                placeholder="记录疗程要点..."
                className="w-full h-32 p-3 border rounded-md resize-none text-sm"
              />
              <Button 
                onClick={handleSaveNotes}
                size="sm"
                className="w-full"
                disabled={!sessionNotes.trim() || isSavingNotes}
              >
                <Save className="h-4 w-4 mr-2" />
                {isSavingNotes ? '保存中...' : '保存笔记'}
              </Button>
            </CardContent>
          </Card>

          {/* 实时聊天 */}
          <Card className="h-1/2">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 justify-between">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  实时聊天
                </div>
                <ChatButton
                  bookingId={bookingId}
                  otherParticipantName={`${client.first_name} ${client.last_name}`}
                  currentUserType="practitioner"
                  unreadCount={unreadCount}
                  onChatOpened={handleChatOpened}
                  size="sm"
                />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-muted-foreground text-center py-8">
                点击上方聊天按钮开始与客户实时聊天
                {unreadCount > 0 && (
                  <p className="text-primary font-medium mt-2">
                    您有 {unreadCount} 条未读消息
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
